import React from 'react'
import { Box } from '@mui/material'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Link, To } from 'react-router-dom'
import { INTERAX_COLORS } from '../../../constants/colors'

export interface NotificationCardProps {
  /** Omit for a notice that only informs — there is nothing useful to open for a slow connection. */
  to?: To
  icon: React.ReactNode
  title: React.ReactNode
  subtitle: React.ReactNode
  onLinkClick?: () => void
  onCloseClick: (e: React.MouseEvent) => void
  isVisible: boolean
  closeButtonAriaLabel: string
  /** Optional: set to true for title that may overflow (ellipsis) */
  titleTruncate?: boolean
}

/**
 * Shared card layout for in-app notifications: icon, title, subtitle, link target, and close button.
 * Handles a11y (focus-visible, aria), reduced-motion, and consistent styling.
 */
export const NotificationCard: React.FC<NotificationCardProps> = ({
  to,
  icon,
  title,
  subtitle,
  onLinkClick,
  onCloseClick,
  isVisible,
  closeButtonAriaLabel,
  titleTruncate = false
}) => {
  const isOpenable = Boolean(to)
  const openableProps = to ? { component: Link, to, onClick: onLinkClick } : {}
  const openableStyles = isOpenable
    ? {
        cursor: 'pointer',
        '&:hover': {
          background:
            'radial-gradient(50.19% 50.19%, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0) 100%), rgba(46, 79, 95, 0.6)'
        },
        '&:focus-visible': {
          outline: `2px solid ${INTERAX_COLORS.sun}`,
          outlineOffset: '2px'
        }
      }
    : {}

  return (
    <Box
      sx={{
        position: 'relative',
        minWidth: { xs: 'calc(100vw - 32px)', sm: '320px', md: '320px' },
        maxWidth: { xs: 'calc(100vw - 32px)', sm: '400px', md: '400px' },
        transition: 'opacity 0.3s ease, transform 0.3s ease',
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateX(0)' : 'translateX(100%)'
      }}
    >
      {/* Clickable link area — does NOT wrap the close button */}
      <Box
        {...openableProps}
        sx={{
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '12px 44px 12px 16px',
          width: '100%',
          background: INTERAX_COLORS.heritageBlueRadial,
          border: `1px solid ${INTERAX_COLORS.white04}`,
          boxShadow: 'inset 0px 1px 0px rgba(255, 255, 255, 0.08), 0px 4px 12px rgba(0, 0, 0, 0.3)',
          borderRadius: '8px',
          textDecoration: 'none',
          color: 'inherit',
          ...openableStyles
        }}
      >
        <Box
          sx={{
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            border: `1.5px dashed ${INTERAX_COLORS.sun}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          {icon}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', flexGrow: 1, minWidth: 0 }}>
          <Box
            sx={{
              fontSize: '14px',
              lineHeight: '20px',
              fontWeight: 500,
              color: INTERAX_COLORS.white,
              ...(titleTruncate ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const } : {})
            }}
          >
            {title}
          </Box>
          <Box sx={{ fontSize: '12px', lineHeight: '16px', fontWeight: 400, color: INTERAX_COLORS.typographyFont }}>
            {subtitle}
          </Box>
        </Box>
      </Box>

      {/* Close button sits OUTSIDE the link so clicks don't trigger navigation */}
      <Box
        component="button"
        type="button"
        onClick={onCloseClick}
        aria-label={closeButtonAriaLabel}
        sx={{
          position: 'absolute',
          top: '50%',
          right: '12px',
          transform: 'translateY(-50%)',
          width: '20px',
          height: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          borderRadius: '4px',
          border: 'none',
          background: 'transparent',
          padding: 0,
          '&:hover': { backgroundColor: INTERAX_COLORS.white10 },
          '&:focus-visible': { outline: `2px solid ${INTERAX_COLORS.sun}`, outlineOffset: '2px' }
        }}
      >
        <XMarkIcon style={{ width: '16px', height: '16px', color: INTERAX_COLORS.white70 }} aria-hidden />
      </Box>
    </Box>
  )
}
